import {
  ContentType,
  grid,
  GridData,
  LucideIconName,
  STATIC_CONTENT_TYPE_NAMES,
} from '@bm-js/atlascloud-shared'
import { v4 as uuid } from 'uuid'
import { Button, Icon } from '@bm-js/atlascloud-ui'
import { PropsWithChildren, useContext, useMemo } from 'react'
import { useEditorActions, useEditorData } from '../../utils/hooks'
import { DragNDropContext } from '../../contexts/DragNDrop.context'
import { useTr } from '../../utils/useTr'
import { TrKey } from '../../cnst/translations/trKey.cnst'

type Props = {
  content: ContentType<GridData>
}

const GridContentWrapper = ({
  children,
  content,
}: PropsWithChildren<Props>) => {
  const { tr } = useTr()
  const { setActiveContent, patchActivePage } = useEditorActions()
  const { activePage } = useEditorData()
  const {
    state: { currentDraggingContent },
  } = useContext(DragNDropContext)

  const hasStaticContent = useMemo(() => {
    return activePage?.content.some((c) =>
      STATIC_CONTENT_TYPE_NAMES.includes(c.name)
    )
  }, [activePage])

  const currentIndex = useMemo(() => {
    return activePage?.content.findIndex((c) => c.uuid === content.uuid) || -1
  }, [activePage, content])

  const moveSection = (dir: 'up' | 'down') => {
    if (!activePage) return
    const newContent = [...activePage.content]
    if (currentIndex === -1) return // Content not found
    if (currentIndex === 0 && dir === 'up') return // Cannot move up if first section
    if (currentIndex === newContent.length - 1 && dir === 'down') return // Cannot move down if last section
    if (currentIndex === 1 && hasStaticContent && dir === 'up') return // Cannot move content up if first content is static

    const [reordered] = newContent.splice(currentIndex, 1)
    newContent.splice(currentIndex + (dir === 'up' ? -1 : 1), 0, reordered)
    patchActivePage({ content: newContent })
  }

  const addSection = (placement: 'above' | 'below') => {
    if (!activePage) return
    const newContent = [...activePage.content]
    if (currentIndex === -1) return // Content not found

    const destinationIndex =
      placement === 'above' ? currentIndex : currentIndex + 1
    newContent.splice(destinationIndex, 0, { ...grid, uuid: uuid() })
    patchActivePage({ content: newContent })
  }

  const commonWrapperClasses = useMemo(() => {
    let classes = 'absolute h-0 left-0 right-0 flex justify-center '
    if (!currentDraggingContent) {
      classes +=
        'opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto'
    }
    return classes
  }, [currentDraggingContent])

  return (
    <div
      className={`group relative ring-primary-400 ${currentDraggingContent ? 'ring-1' : 'ring-0 hover:ring-1'}`}
    >
      <div
        onDoubleClick={() => setActiveContent(content.uuid)}
        className='min-h-[5rem]'
      >
        {children}
      </div>
      <>
        <div className={`-top-px items-start ${commonWrapperClasses}`}>
          <div className='flex gap-1'>
            <Button
              withIcon
              onClick={() => moveSection('up')}
              title={tr(TrKey.editorCenterMoveSectionUp)}
            >
              <Icon name={LucideIconName.CircleArrowUp} className='size-5' />
            </Button>
            <Button
              withIcon
              title={tr(TrKey.editorCenterAddSectionAbove)}
              onClick={() => addSection('above')}
            >
              <Icon name={LucideIconName.CirclePlus} className='size-5' />
            </Button>
            <Button
              withIcon
              title={tr(TrKey.editorCenterEditSection)}
              onClick={() => setActiveContent(content.uuid)}
            >
              <Icon name={LucideIconName.Pen} className='size-5' />
            </Button>
          </div>
        </div>
        <div className={`-bottom-px items-end ${commonWrapperClasses}`}>
          <div className='flex gap-1'>
            <Button
              withIcon
              onClick={() => moveSection('down')}
              title={tr(TrKey.editorCenterMoveSectionDown)}
            >
              <Icon name={LucideIconName.CircleArrowDown} className='size-5' />
            </Button>
            <Button
              withIcon
              onClick={() => addSection('below')}
              title={tr(TrKey.editorCenterAddSectionBelow)}
            >
              <Icon name={LucideIconName.CirclePlus} className='size-5' />
            </Button>
          </div>
        </div>
      </>
    </div>
  )
}

export default GridContentWrapper
